// ---------------------------------------------------------------------------
// Spain's official river basin/sub-basin boundaries (MITECO, Ministerio
// para la Transición Ecológica) — two reference layers for visual
// comparison on the map, unrelated to any EvalHid project's own data.
// MITECO's own live WMS for these proved unreliable — repeated connection
// resets — so both serve a static, pre-processed copy of the source
// shapefiles instead, stripped to just {basinCode, name} before shipping.
// ---------------------------------------------------------------------------

import { geometryBounds, geometryCentroid, pointInPolygon } from "./gisLayer.ts";

export interface MitecoBasinProperties {
  basinCode: string;
  name: string;
}

/**
 * Disambiguates `name` against name collisions before importing a batch of
 * selected MITECO features as sub-basins (App.tsx's addSelectedMiteco).
 * MITECO names each sub-basin after the river/stream it drains, not the
 * catchment itself, so the same name (e.g. "EBRO") is reused across many
 * different polygons along that river — confirmed against the real data,
 * ~58% of the national sub-basin catalog shares its name with at least one
 * other feature. Since sub-basins are linked to their precipitation/ETP
 * columns by exact name (see PasteDailySeries.tsx/dailySeries.ts), two
 * sub-basins ending up with the same name would make that link ambiguous.
 *
 * Collisions get MITECO's own `basinCode` appended — stable and traceable
 * back to MITECO's catalog, unlike an arbitrary counter — checked against
 * both `existingNames` (the project's current sub-basins) and the rest of
 * this same batch (two colliding names can arrive in one "Add N as
 * sub-basins" click).
 */
export function disambiguateMitecoNames(
  features: GeoJSON.Feature[],
  existingNames: ReadonlySet<string>
): GeoJSON.Feature<GeoJSON.Geometry, MitecoBasinProperties>[] {
  const used = new Set(existingNames);
  return features.map((f) => {
    const { name: rawName, basinCode } = f.properties as MitecoBasinProperties;
    const name = used.has(rawName) ? `${rawName} (${basinCode})` : rawName;
    used.add(name);
    return { ...f, properties: { name, basinCode } } as GeoJSON.Feature<
      GeoJSON.Geometry,
      MitecoBasinProperties
    >;
  });
}

/**
 * "Add sub-basins from MITECO" mode (SubBasinsPanel.tsx's own button, next
 * to "Draw a sub-basin") — click a polygon or drag a rectangle (Shift
 * adds/Ctrl removes, same convention as the project's own sub-basin
 * selection) to build up a working set of MitecoBasinProperties-tagged
 * features, keyed by basinCode, then "Add N as sub-basins" commits all of
 * them at once (see App.tsx's addSelectedMiteco). Only one of the two
 * layers is shown at a time — `layer` picks which.
 */
export interface MapMitecoMode {
  layer: "basins" | "subBasins";
  selected: Map<string, GeoJSON.Feature>;
  onChangeSelected: (selected: Map<string, GeoJSON.Feature>) => void;
  /** basinCode values already imported into this project on this same layer (see storage.ts's MitecoOrigin) — MitecoSelectableLayer.tsx marks these distinctly and won't let them be (re)selected, since there's no reliable geometry-based duplicate check for MITECO polygons (see addSelectedMiteco). */
  alreadyAdded: ReadonlySet<string>;
}

// -----------------------------------------------------------------------
// Sub-basins (MitecoSubBasinsLayer.tsx) — 12,162 for the whole country,
// too many to ship as one file (raw shapefile was ~46MB; simplified and
// trimmed, still ~4.7MB total). Split by hydrographic demarcation instead
// — see scripts/prepare-miteco-subbasins.cjs — so a typical small project
// only ever fetches the one or two regions it actually overlaps.
// -----------------------------------------------------------------------

interface MitecoRegion {
  id: string;
  /** [minLng, minLat, maxLng, maxLat] */
  bounds: [number, number, number, number];
  url: string;
  count: number;
}

const MANIFEST_URL = "/subcuencas-miteco/manifest.json";

let manifestPromise: Promise<MitecoRegion[]> | null = null;

/**
 * `??=` caches whatever promise the FIRST call creates — if that fetch
 * fails (network blip, transient 5xx, offline), the rejected promise would
 * otherwise stay cached forever (`??=` never re-assigns once the variable
 * is non-null, rejected or not), permanently blanking the MITECO map picker
 * for the rest of the session with no way to recover short of a page
 * reload. Clearing the cache on rejection lets the next call actually
 * retry against the network — same fix as aemet.ts's fetchStationInventory.
 */
export function fetchMitecoManifest(): Promise<MitecoRegion[]> {
  manifestPromise ??= fetch(MANIFEST_URL)
    .then((r) => r.json())
    .catch((err) => {
      manifestPromise = null;
      throw err;
    });
  return manifestPromise;
}

/** Axis-aligned bbox intersection — regions are whole hydrographic demarcations, not an exact clip of the viewport, but close enough to avoid fetching the other 14 regions. */
export function regionIntersectsBounds(
  region: MitecoRegion,
  viewBounds: [number, number, number, number]
): boolean {
  const [minLng, minLat, maxLng, maxLat] = region.bounds;
  const [vMinLng, vMinLat, vMaxLng, vMaxLat] = viewBounds;
  return minLng <= vMaxLng && maxLng >= vMinLng && minLat <= vMaxLat && maxLat >= vMinLat;
}

/**
 * Appends `newlyLoaded` region ids to `current`, deduped — MitecoSubBasinsLayer.tsx's
 * `setLoadedIds` updater. Needs to be dedup-safe (not a plain append) because
 * a fast pan/zoom can leave a stale `moveend` listener attached until React
 * commits the effect that replaces it, and if it fires again in that window
 * it recomputes the same "needed" list and would otherwise append the same
 * id a second time — rendering that region's polygons twice on the map.
 * Returns `current` unchanged (same reference) when there's nothing new, so
 * a duplicate/no-op call doesn't even trigger a re-render.
 */
export function withLoadedRegionIds(current: string[], newlyLoaded: string[]): string[] {
  const additions = newlyLoaded.filter((id) => !current.includes(id));
  return additions.length > 0 ? [...current, ...additions] : current;
}

const regionCache = new Map<string, GeoJSON.FeatureCollection>();

export async function fetchMitecoRegion(region: MitecoRegion): Promise<GeoJSON.FeatureCollection> {
  const cached = regionCache.get(region.id);
  if (cached) return cached;
  const data = (await fetch(region.url).then((r) => r.json())) as GeoJSON.FeatureCollection;
  regionCache.set(region.id, data);
  return data;
}

/**
 * Every sub-basin region whose bbox intersects `bounds` — same per-region
 * fetch/cache as MitecoSubBasinsLayer.tsx's own viewport-driven loading, but
 * for a FIXED bbox instead of the live map view. Used by `subBasinsForBasin`
 * below, which needs every sub-basin under one selected basin regardless of
 * what's currently panned into view. Not exported: nothing outside this file
 * needs the raw region-fetch step on its own, only the full lookup.
 */
async function fetchMitecoRegionsForBounds(
  bounds: [number, number, number, number]
): Promise<GeoJSON.Feature[]> {
  const manifest = await fetchMitecoManifest();
  const needed = manifest.filter((region) => regionIntersectsBounds(region, bounds));
  const fetched = await Promise.all(needed.map(fetchMitecoRegion));
  return fetched.flatMap((r) => r.features);
}

// -----------------------------------------------------------------------
// Basins (MitecoBasinsLayer.tsx) — only 118 for the whole country, small
// enough to ship as a single ~280KB file, no splitting needed — see
// scripts/prepare-miteco-cuencas.cjs.
// -----------------------------------------------------------------------

const BASINS_URL = "/cuencas-miteco/cuencas.json";

let basinsPromise: Promise<GeoJSON.FeatureCollection> | null = null;

/** Same rejected-promise-cached-forever trap as `fetchMitecoManifest` above — see its comment. */
export function fetchMitecoBasins(): Promise<GeoJSON.FeatureCollection> {
  basinsPromise ??= fetch(BASINS_URL)
    .then((r) => r.json())
    .catch((err) => {
      basinsPromise = null;
      throw err;
    });
  return basinsPromise;
}

// -----------------------------------------------------------------------
// Basin → sub-basins spatial lookup — the two layers above use unrelated
// code schemes (basins.basinCode is NOT a foreign key into any sub-basin
// property), so "which sub-basins belong to this basin" has no shortcut
// through the data itself and has to be worked out geometrically. Used by
// the "MITECO" wizard tab (Step1SubBasins.tsx): pick one basin, pull in
// every sub-basin that falls inside it.
// -----------------------------------------------------------------------

/**
 * Sub-basins from `subBasins` whose centroid falls inside `basin`'s own
 * polygon — see gisLayer.ts's `pointInPolygon` for the
 * centroid-vs-full-intersection tradeoff (cheap, wrong only for a sliver of
 * a sub-basin straddling the basin boundary).
 */
export function findSubBasinsInBasin(
  basin: GeoJSON.Feature,
  subBasins: GeoJSON.Feature[]
): GeoJSON.Feature[] {
  return subBasins.filter((sub) => {
    const centroid = geometryCentroid(sub.geometry);
    return centroid !== null && pointInPolygon(centroid, basin.geometry);
  });
}

/**
 * End-to-end: fetches only the sub-basin region(s) whose bbox overlaps
 * `basin` (usually one, sometimes two for a basin straddling a
 * hydrographic demarcation boundary) and returns the ones that actually
 * fall inside it.
 */
export async function subBasinsForBasin(basin: GeoJSON.Feature): Promise<GeoJSON.Feature[]> {
  const bounds = geometryBounds(basin.geometry);
  if (!bounds) return [];
  const [[minLat, minLng], [maxLat, maxLng]] = bounds;
  const candidates = await fetchMitecoRegionsForBounds([minLng, minLat, maxLng, maxLat]);
  return findSubBasinsInBasin(basin, candidates);
}
